// API Endpoint definitions for Prosci Connect API
// Base URL: /api

export const API_ENDPOINTS = {
  // User endpoints
  users: {
    show: (id: string) => `/api/users/${id}`,
    create: () => `/api/users`,
    update: (id: string) => `/api/users/${id}`,
    destroy: (id: string) => `/api/users/${id}`,
    verifyEmailChange: () => `/api/users/verify_email_change`,
    findByEmail: () => `/api/users/find/email`,
    destroyProjects: (id: string) => `/api/users/${id}/projects`,
    findByAuth0Sub: () => `/api/users/find/auth0_sub`,
    emailChangeRequest: (id: string) => `/api/users/${id}/email_change_request`,
    elearningStatus: (userId: string, productCode: string) => `/api/users/${userId}/elearning_statuses/${productCode}`,
    elearningStatusById: (userId: string, productId: string) => `/api/users/${userId}/elearning_statuses/${productId}`,
    products: (userId: string) => `/api/users/${userId}/products`,
  },

  // Account endpoints
  accounts: {
    show: (id: string) => `/api/accounts/${id}`,
    domainLookup: () => `/api/accounts/find/domain`,
    slugLookup: () => `/api/accounts/find/slug`,
    sendNotification: (id: string) => `/api/accounts/${id}/send_notification`,
    products: (accountId: string) => `/api/accounts/${accountId}/products`,
    productShow: (accountId: string, productId: string) => `/api/accounts/${accountId}/products/${productId}`,
  },

  // Project endpoints
  projects: {
    show: (id: string) => `/api/projects/${id}`,
    create: () => `/api/projects`,
    update: (id: string) => `/api/projects/${id}`,
    destroy: (id: string) => `/api/projects/${id}`,
    duplicateProject: () => `/api/duplicate_project`,
    projectDashboard: (id: string) => `/api/project_dashboard/${id}`,
    projectSummary: (id: string) => `/api/project_summary/${id}`,
    projectFlowProjects: (userId: string) => `/api/project_flow_projects/${userId}`,
    createFromTemplate: () => `/api/create_project_from_template`,
    projectTemplates: () => `/api/project_templates`,
    addHiddenNavElement: (id: string) => `/api/projects/${id}/add_hidden_nav_element`,
    removeHiddenNavElement: (id: string) => `/api/projects/${id}/remove_hidden_nav_element`,
    importResources: (projectId: string, resource: string) => `/api/projects/${projectId}/import_resources/${resource}`,
    guideStatuses: (projectId: string) => `/api/projects/${projectId}/guide_statuses`,
  },

  // Activity endpoints
  activities: {
    create: () => `/api/activities`,
  },

  // User Login endpoints
  userLogins: {
    create: () => `/api/user_logins`,
  },

  // Proxima Assessment endpoints
  proximaAssessments: {
    show: (id: string) => `/api/proxima_assessments/${id}`,
    create: () => `/api/proxima_assessments`,
    update: (id: string) => `/api/proxima_assessments/${id}`,
    destroy: (id: string) => `/api/proxima_assessments/${id}`,
    exportChangeImpactAssessmentResponses: () => `/api/proxima_assessments/export_change_impact_assessment_responses`,
    importChangeImpactAssessmentResponses: () => `/api/proxima_assessments/import_change_impact_assessment_responses`,
  },

  // Proxima Assessment Survey endpoints
  proximaAssessmentSurveys: {
    index: () => `/api/proxima_assessment_surveys`,
    show: (id: string) => `/api/proxima_assessment_surveys/${id}`,
    create: () => `/api/proxima_assessment_surveys`,
    update: (id: string) => `/api/proxima_assessment_surveys/${id}`,
    destroy: (id: string) => `/api/proxima_assessment_surveys/${id}`,
  },

  // Change Management Performance Report endpoints
  changeManagementPerformanceReports: {
    show: (id: string) => `/api/change_management_performance_reports/${id}`,
    create: () => `/api/change_management_performance_reports`,
    update: (id: string) => `/api/change_management_performance_reports/${id}`,
    destroy: (id: string) => `/api/change_management_performance_reports/${id}`,
  },

  // Proxima Activity endpoints
  proximaActivities: {
    index: () => `/api/proxima_activities`,
    show: (id: string) => `/api/proxima_activities/${id}`,
    create: () => `/api/proxima_activities`,
    update: (id: string) => `/api/proxima_activities/${id}`,
    destroy: (id: string) => `/api/proxima_activities/${id}`,
  },

  // Resistance endpoints
  resistances: {
    index: () => `/api/resistances`,
    show: (id: string) => `/api/resistances/${id}`,
    create: () => `/api/resistances`,
    update: (id: string) => `/api/resistances/${id}`,
    destroy: (id: string) => `/api/resistances/${id}`,
  },

  // Role endpoints
  roles: {
    index: () => `/api/roles`,
    show: (id: string) => `/api/roles/${id}`,
    create: () => `/api/roles`,
    update: (id: string) => `/api/roles/${id}`,
    destroy: (id: string) => `/api/roles/${id}`,
  },

  // Release Date endpoints
  releaseDates: {
    index: () => `/api/release_dates`,
    show: (id: string) => `/api/release_dates/${id}`,
    create: () => `/api/release_dates`,
    update: (id: string) => `/api/release_dates/${id}`,
    destroy: (id: string) => `/api/release_dates/${id}`,
  },

  // Public Link endpoints
  publicLinks: {
    index: () => `/api/public_links`,
    show: (id: string) => `/api/public_links/${id}`,
    create: () => `/api/public_links`,
    update: (id: string) => `/api/public_links/${id}`,
    destroy: (id: string) => `/api/public_links/${id}`,
  },

  // Export endpoints
  exports: {
    index: () => `/api/exports`,
    show: (id: string) => `/api/exports/${id}`,
    create: () => `/api/exports`,
    update: (id: string) => `/api/exports/${id}`,
    destroy: (id: string) => `/api/exports/${id}`,
  },

  // Item Event endpoints
  itemEvents: {
    index: () => `/api/item_events`,
    create: () => `/api/item_events`,
    update: (id: string) => `/api/item_events/${id}`,
    destroy: (id: string) => `/api/item_events/${id}`,
  },

  // ADKAR Element endpoints
  adkarElements: {
    update: (id: string) => `/api/adkar_elements/${id}`,
  },

  // Associated Training Contact endpoints
  associatedTrainingContacts: {
    show: (id: string) => `/api/associated_training_contacts/${id}`,
    update: (id: string) => `/api/associated_training_contacts/${id}`,
  },

  // Bookmark endpoints
  bookmarks: {
    show: (id: string) => `/api/bookmarks/${id}`,
    create: () => `/api/bookmarks`,
    update: (id: string) => `/api/bookmarks/${id}`,
    destroy: (id: string) => `/api/bookmarks/${id}`,
  },

  // Impacted Group endpoints
  impactedGroups: {
    show: (id: string) => `/api/impacted_groups/${id}`,
    create: () => `/api/impacted_groups`,
    update: (id: string) => `/api/impacted_groups/${id}`,
    destroy: (id: string) => `/api/impacted_groups/${id}`,
  },

  // Plan Type endpoints
  planTypes: {
    update: (id: string) => `/api/plan_types/${id}`,
    destroy: (id: string) => `/api/plan_types/${id}`,
  },

  // Impacted Group Proxima Assessment endpoints
  impactedGroupProximaAssessments: {
    create: () => `/api/impacted_group_proxima_assessments`,
    update: (id: string) => `/api/impacted_group_proxima_assessments/${id}`,
    destroy: (id: string) => `/api/impacted_group_proxima_assessments/${id}`,
  },

  // Tracking Calendar endpoints
  trackingCalendars: {
    show: (id: string) => `/api/tracking_calendars/${id}`,
  },

  // Project Assignment endpoints
  projectAssignments: {
    create: () => `/api/project_assignments`,
    update: (id: string) => `/api/project_assignments/${id}`,
    destroy: (id: string) => `/api/project_assignments/${id}`,
  },

  // Email endpoints
  email: {
    create: () => `/api/email`,
  },

  // Resource Drag Order endpoints
  resourceDragOrders: {
    create: () => `/api/resource_drag_orders`,
    update: (id: string) => `/api/resource_drag_orders/${id}`,
  },

  // Key Impacted Group endpoints
  keyImpactedGroups: {
    create: () => `/api/key_impacted_groups`,
  },

  // View endpoints
  views: {
    create: () => `/api/views`,
    update: (id: string) => `/api/views/${id}`,
    destroy: (id: string) => `/api/views/${id}`,
  },

  // Group Tag endpoints
  groupTags: {
    create: () => `/api/group_tags`,
    update: (id: string) => `/api/group_tags/${id}`,
    destroy: (id: string) => `/api/group_tags/${id}`,
  },

  // Admin endpoints
  admins: {
    show: (id: string) => `/api/admins/${id}`,
  },

  // E-learning Status endpoints
  elearningStatuses: {
    show: (id: string) => `/api/elearning_statuses/${id}`,
    create: () => `/api/elearning_statuses`,
    update: (id: string) => `/api/elearning_statuses/${id}`,
    sendFailureNotification: (id: string) => `/api/elearning_statuses/${id}/send_failure_notification`,
  },

  // AI Services endpoints
  aiServices: {
    createChatSession: () => `/api/ai_services/create_chat_session`,
    createOrUpdateAIChatSession: () => `/api/ai_services/create_or_update_ai_chat_session`,
    sendChatResponseFeedback: () => `/api/ai_services/send_chat_response_feedback`,
    sendChatMessage: () => `/api/ai_services/send_chat_message`,
    getUserChatSessions: () => `/api/ai_services/get_user_chat_sessions`,
    getChatSessionMessages: () => `/api/ai_services/get_chat_session_messages`,
    deleteChatSession: () => `/api/ai_services/delete_chat_session`,
    updateChatSession: () => `/api/ai_services/update_chat_session`,
  },

  // Portal Token endpoints
  portalTokens: {
    verify: (token: string) => `/api/portal_tokens/verify/${token}`,
  },
  
  // Admin endpoints
  admin: {
    me: () => `/api/admin/me`,
    findByAuth0Sub: () => `/api/admin/find/auth0_sub`,
  },

  // Seeder endpoint (dev/staging only)
  seeder: {
    seed: () => `/api/seeder`,
  },
} as const;

// HTTP Method types
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// API Request configuration
export interface ApiRequestConfig {
  method: HttpMethod;
  endpoint: string;
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  // Suppress the expired-session flow for this request. Use for background/init
  // polls (chameleon, intercom, masquerade status) where an expected 401 must
  // not trigger a re-login. Revocation is still handled unconditionally.
  skip401Handling?: boolean;
}

// Helper to build query strings
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(`${key}[]`, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

// Helper to build full URL with query parameters
export function buildUrl(endpoint: string, params?: Record<string, any>): string {
  if (!params) return endpoint;
  return `${endpoint}${buildQueryString(params)}`;
}